import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChatCircleDots, Plus, PushPin, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

export const renderMentions = (text, mine) =>
  (text || "").split(/(@[\w.\-]+)/g).map((part, i) =>
    part.startsWith("@") ? (
      <span
        key={i}
        className={`rounded px-1 font-semibold ${
          mine ? "bg-white/20 text-white" : "bg-indigo-500/20 text-indigo-200"
        }`}
      >
        {part}
      </span>
    ) : (
      part
    ),
  );

const Bubble = ({ note, mine, pinned, onDelete, onPin }) => (
  <div
    data-testid={`team-note-${note.id}`}
    className={`flex ${mine ? "justify-end" : "justify-start"}`}
  >
    <div
      className={`group relative max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
        mine
          ? "rounded-br-sm bg-indigo-500/90 text-white"
          : "rounded-bl-sm border border-zinc-800 bg-zinc-900 text-zinc-100"
      } ${pinned ? "ring-1 ring-amber-400/70" : ""}`}
    >
      <div className="whitespace-pre-wrap break-words">{renderMentions(note.text, mine)}</div>
      <div
        className={`mt-1 flex items-center gap-2 text-[10px] uppercase tracking-widest ${
          mine ? "text-indigo-100/80" : "text-zinc-500"
        }`}
      >
        <span>{note.author || "System"}</span>
        <span>·</span>
        <span>{when(note.at)}</span>
        {pinned && <PushPin size={11} weight="fill" className="text-amber-300" />}
        {onPin && (
          <button
            type="button"
            data-testid={`team-note-pin-${note.id}`}
            onClick={onPin}
            title={pinned ? "Unpin" : "Pin to project header"}
            className={`ml-1 transition-opacity ${pinned ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <PushPin size={11} weight="bold" />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            data-testid={`team-note-delete-${note.id}`}
            onClick={onDelete}
            title="Delete note"
            className="opacity-0 transition-opacity group-hover:opacity-100"
          >
            <Trash size={11} weight="bold" />
          </button>
        )}
      </div>
    </div>
  </div>
);

export const TeamNotes = ({ p, id, setP }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [crew, setCrew] = useState([]);
  const taRef = useRef(null);
  const notes = p.team_notes || [];
  const legacy = (p.notes || "").trim();

  useEffect(() => {
    if (!open || crew.length) return;
    api.get("/crew-list").then(({ data }) => setCrew(data || [])).catch(() => {});
  }, [open, crew.length]);

  const token = (() => {
    const el = taRef.current;
    const pos = el ? el.selectionStart : text.length;
    const m = text.slice(0, pos).match(/@([\w.\-]*)$/);
    return m ? m[1].toLowerCase() : null;
  })();
  const suggestions =
    token === null
      ? []
      : crew
          .filter((u) => u.email !== user?.email)
          .filter((u) => (u.name || u.email).toLowerCase().includes(token))
          .slice(0, 6);

  const insertMention = (u) => {
    const el = taRef.current;
    const pos = el ? el.selectionStart : text.length;
    const before = text.slice(0, pos).replace(/@[\w.\-]*$/, "");
    const handle = (u.name || u.email).split(" ")[0];
    setText(`${before}@${handle} ${text.slice(pos)}`);
    setTimeout(() => el?.focus(), 0);
  };

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/team-notes`, { text });
      setP(data);
      setText("");
      setOpen(false);
      toast.success("Note added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add note");
    }
    setBusy(false);
  };

  const remove = async (nid) => {
    try {
      const { data } = await api.delete(`/projects/${id}/team-notes/${nid}`);
      setP(data);
    } catch {
      toast.error("Could not delete note");
    }
  };

  const pin = async (nid) => {
    try {
      const { data } = await api.post(`/projects/${id}/team-notes/${nid}/pin`);
      setP(data);
      toast.success(data.pinned_note_id === nid ? "Pinned to project header" : "Unpinned");
    } catch {
      toast.error("Could not pin note");
    }
  };

  return (
    <div className="mt-3" data-testid="team-notes">
      <div className="max-h-[420px] space-y-3 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
        {legacy && (
          <Bubble note={{ id: "legacy", text: legacy, author: "Imported", at: p.created_at }} />
        )}
        {notes.length === 0 && !legacy && (
          <p className="text-xs text-zinc-500" data-testid="team-notes-empty">
            No team notes yet — add the first one.
          </p>
        )}
        {notes.map((n) => (
          <Bubble
            key={n.id}
            note={n}
            mine={n.author_email && n.author_email === user?.email}
            pinned={p.pinned_note_id === n.id}
            onDelete={() => remove(n.id)}
            onPin={() => pin(n.id)}
          />
        ))}
      </div>
      <Button
        type="button"
        data-testid="team-note-add-button"
        onClick={() => setOpen(true)}
        className="mt-3 h-10 gap-2 rounded-full bg-indigo-500 px-5 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
      >
        <Plus size={14} weight="bold" /> Add note
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-zinc-800 bg-zinc-950 sm:max-w-lg" data-testid="team-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ChatCircleDots size={18} weight="bold" className="text-indigo-300" /> Team note
            </DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Textarea
              ref={taRef}
              autoFocus
              data-testid="team-note-input"
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                if (e.key === "Tab" && suggestions.length) {
                  e.preventDefault();
                  insertMention(suggestions[0]);
                }
              }}
              placeholder="Internal note — type @ to mention a teammate"
              className="border-zinc-800 bg-zinc-900 text-sm"
            />
            {suggestions.length > 0 && (
              <div
                data-testid="mention-suggestions"
                className="absolute left-0 top-full z-20 mt-1 w-64 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 shadow-xl"
              >
                {suggestions.map((u) => (
                  <button
                    key={u.id || u.email}
                    type="button"
                    data-testid={`mention-option-${(u.name || u.email).split(" ")[0].toLowerCase()}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(u);
                    }}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-indigo-500/15"
                  >
                    <span className="font-semibold text-zinc-100">{u.name || u.email}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{u.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className="h-10 border-zinc-700" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="team-note-send"
              disabled={busy || !text.trim()}
              onClick={send}
              className="h-10 bg-indigo-500 px-6 text-xs font-bold uppercase tracking-widest hover:bg-indigo-400"
            >
              {busy ? "Posting…" : "Post"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const PinnedNote = ({ p }) => {
  const n = (p.team_notes || []).find((x) => x.id === p.pinned_note_id);
  if (!n) return null;
  return (
    <div
      data-testid="project-pinned-note"
      className="mt-3 flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-50"
    >
      <PushPin size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-300" />
      <div className="min-w-0">
        <div className="whitespace-pre-wrap break-words">{renderMentions(n.text)}</div>
        <div className="mt-0.5 text-[10px] uppercase tracking-widest text-amber-300/80">
          {n.author} · {when(n.at)}
        </div>
      </div>
    </div>
  );
};
